import React, { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { generateGoalPeriodTimeline } from "@signolteam/shared/utils";
import { getBehaviourColourForPosition } from "@signolteam/shared/colours";
import { AppUtils } from "@src/utils";
import AdminHooks from "@src/context/hooks/adminHooks";
import {
  BackButton,
  Button,
  CustomerDropDown,
  DropDown,
  FormInput,
  Loader,
  Tabs,
  TabSelector,
  Tag,
} from "@src/components/atoms";
import ErrorPage from "@src/components/layout/ErrorPage";
import { CardContainer } from "@src/components/cards";
import ReportsServiceSnsForm from "./ReportsServiceSnsForm";
import { useAdminContext } from "@src/context/adminContext";

const GoalPeriodTimelineTable = () => {
  const { customers } = useAdminContext();
  const [goalPeriodTimeline, setGoalPeriodTimeline] = useState({});
  const [loading, setLoading] = useState(true);
  const todayRowRef = useRef(null);
  const today = moment();

  /** useEffect: Do the hard work building the timeline (behind setTimeout 0 to reduce freezing the browser) */
  useEffect(() => {
    setLoading(true);
    const timeout = setTimeout(() => {
      const monthlyReportingDelays = customers
        .filter((c) => c.isActive && c.industry === "aviation")
        .flatMap((c) => c.reportingDelay);

      const biweeklyReportingDelays = customers
        .filter((c) => c.isActive && c.industry === "shipping")
        .flatMap((c) => c.reportingDelay);

      const startOfLastMonth = moment().subtract(1, "month").startOf("month");
      const endOfYear = moment().endOf("year");

      const timeline = generateGoalPeriodTimeline(
        startOfLastMonth.toDate(),
        endOfYear.toDate(),
        biweeklyReportingDelays,
        monthlyReportingDelays,
      );

      setGoalPeriodTimeline(timeline);
      setLoading(false);
    }, 0);

    return () => clearTimeout(timeout);
  }, [customers]);

  /** useEffect: Scroll to "today" */
  useEffect(() => {
    if (!loading && todayRowRef.current) {
      todayRowRef.current.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });
    }
  }, [loading, goalPeriodTimeline]);

  if (loading) {
    return <Loader size="xl" />;
  }

  return (
    <CardContainer
      padding={4}
      className="max-h-[70vh] overflow-y-scroll text-sm"
    >
      <table>
        <thead>
          <tr className="bg-primary-100">
            <th className="p-2">Day</th>
            <th className="p-2">Events</th>
          </tr>
        </thead>
        <tbody>
          {Object.entries(goalPeriodTimeline).map(([date, entries]) => {
            const dayMoment = moment(date);
            const isPast = dayMoment.isBefore(today, "day");
            const isToday = !isPast && dayMoment.isSame(today, "day");

            return (
              <tr
                key={date}
                ref={isToday ? todayRowRef : null}
                className={`border-t ${
                  isPast
                    ? "bg-neutral-100"
                    : isToday
                      ? "bg-primary-200 font-medium"
                      : ""
                }`}
              >
                <td className="p-2 whitespace-nowrap">
                  {date} ({dayMoment.format("ddd Do MMM")})
                </td>
                <td className="p-2 space-y-1">
                  {entries.length === 0 ? (
                    <span className="text-gray-400">—</span>
                  ) : (
                    entries.map((entry, idx) => (
                      <Tag
                        key={idx}
                        color={
                          getBehaviourColourForPosition(
                            (entry.reportingDelay % 8) + 1,
                          ).name
                        }
                      >
                        {entry.type} {entry.periodDuration ?? ""}{" "}
                        {entry.reportingDelay ? `${entry.reportingDelay}d` : ""}
                      </Tag>
                    ))
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </CardContainer>
  );
};
export default GoalPeriodTimelineTable;
